import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { Schema as SchemaType } from 'effect';
import { Effect, Schema } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { HTTP_METHOD } from './_shared';

const ENDPOINT = '/api/v2/shop/product-variants/{code}/price-history';
export type MakeGetProductVariantPriceHistoryDeps<
  ProductVariantPriceHistory extends SchemaType.Top,
> = {
  readonly schemas: {
    readonly productVariantPriceHistory: ProductVariantPriceHistory;
  };
  readonly rawUrlEncode: (input: string) => string;
};

export const makeGetProductVariantPriceHistory = <
  ProductVariantPriceHistory extends SchemaType.Top,
>(
  deps: MakeGetProductVariantPriceHistoryDeps<ProductVariantPriceHistory>
) => {
  const GetProductVariantPriceHistoryError = createBackendApiError({
    domain: 'ProductVariant',
    endpoint: 'GetPriceHistory',
    path: ENDPOINT,
    identifierField: 'code',
  });

  const getProductVariantPriceHistoryFetch = (code: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/json')
        .fetch({ params: { code: deps.rawUrlEncode(code) } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'productVariant.code': code,
        },
      })
    );

  const getProductVariantPriceHistory = (code: string) =>
    getProductVariantPriceHistoryFetch(code).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(Schema.Array(deps.schemas.productVariantPriceHistory))(response),
          404: (response) =>
            Effect.fail(new GetProductVariantPriceHistoryError.NotFoundError({ code, response })),
          orElse: (response) =>
            Effect.fail(new GetProductVariantPriceHistoryError.ApiRequestError({ code, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductVariantGet,
          'Debug',
          `Successfully resolved price history for product variant: ${code} -> ${String(result.length)} items`
        )
      ),
      Effect.withSpan('api.productVariant.getPriceHistory', {
        kind: 'internal',
        attributes: {
          'productVariant.code': code,
        },
      })
    );

  return { getProductVariantPriceHistory, GetProductVariantPriceHistoryError };
};
